<template>
  <div class="form-wrapper">
    <form id="financing-form" class="financing-form" @submit.prevent="">
      <form-select
        id="leaseType"
        class="product-type"
        data-qa="lease-product-type-dropdown"
        :model-value="financialParams.leaseType"
        :wrapper-class="'input'"
        :options="productTypes.lease"
        :title="retrieveModalText('leasetypelabel')"
        :is-read-only="true"
        :error-message="noPrivateLeaseErrorMessage"
      />
      <form-select
        v-if="!isOperationalLease"
        id="province"
        class="product-type"
        :model-value="financialParams.province"
        :wrapper-class="'input'"
        :options="productTypes.province"
        :title="retrieveModalText('provincelabel')"
        :is-read-only="disableFields"
        :null-value-on-empty="true"
        @change="$emit('updateFinancing', 'province', $event)"
      />
      <div v-if="errorMessages" class="error-message">
        <div v-for="({ type, source, message }, index) in errorMessages" :key="index">
          {{ `${type} ${source}: ${message}` }}
        </div>
      </div>
      <form-radio-toggle
        id="duration"
        data-qa="duration"
        :title="retrieveModalText('durationlabel')"
        :block-label="true"
        :options="productTypes.duration"
        :model-value="financialParams.duration"
        :disabled="disableFields"
        :disabled-options="durationDisabledOptions"
        @change="$emit('updateFinancing', 'duration', $event)"
      />
      <horizontal-line />
      <form-radio-toggle
        id="yearlyKm"
        data-qa="yearly-km"
        :title="retrieveModalText('yearlyKmlabel')"
        :block-label="true"
        :model-value="financialParams.yearlyKm"
        :options="productTypes.yearlyKm"
        :disabled="disableFields"
        :disabled-options="yearlyKmDisabledOptions"
        @change="$emit('updateFinancing', 'yearlyKm', $event)"
      />
      <span class="financing-form__info-text" v-html="yearlyKmInfoText"></span>
      <span class="financing-form__info-text" v-html="lessYearlyKmInfoText"></span>
      <horizontal-line v-if="isOperationalLease" />
      <price-input
        v-if="isOperationalLease"
        id="downPayment-operational-lease"
        v-model="financialParams.downPayment"
        name="downPayment"
        :title="retrieveModalText('downPaymentLabel')"
        class="has-info payment-input"
        :tab-on-enter="true"
        tabindex="1"
        :fraction-digits="0"
        :truncate="true"
        :is-read-only="disableFields"
        @change="$emit('updateFinancing', 'downPayment', $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('downpaymenttitle')"
            :content="retrieveInfoText('downpaymenttext')"
            data-qa="lease-down-payment-info-icon"
          />
        </template>
      </price-input>
      <horizontal-line />
      <form-radio-button-list
        id="tire"
        data-qa="tires"
        :title="retrieveModalText('tirelabel')"
        :options="productTypes.tires"
        :model-value="financialParams.tireProduct"
        :disabled="disableFields"
        @change="changeTyresSelection($event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('tireinfotitle')"
            :content="retrieveInfoText('tireinfotext')"
            data-qa="tire-info-icon"
          />
        </template>
      </form-radio-button-list>
      <div>
        <tyres-accessories ref="tyresAccessories" :visible="allTyresOptionSelected" />
      </div>
      <form-radio-button-list
        id="reliefVehicle"
        data-qa="relief-vehicle"
        :title="retrieveModalText('reliefVehiclelabel')"
        :options="productTypes.reliefVehicle"
        :model-value="financialParams.reliefVehicleProduct"
        :disabled="disableFields"
        @change="$emit('updateFinancing', 'reliefVehicleProduct', $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('reliefvehicleinfotitle')"
            :content="retrieveInfoText('reliefvehicleinfotext')"
            data-qa="relief-vehicle-info-icon"
          />
        </template>
      </form-radio-button-list>
      <horizontal-line v-if="isOperationalLease" />
      <form-radio-button-list
        v-if="isOperationalLease"
        id="insurance"
        data-qa="insurance"
        :title="retrieveModalText('insurancelabel')"
        :options="productTypes.insuranceProduct"
        :model-value="financialParams.insuranceProduct"
        :disabled="disableFields"
        @change="$emit('updateFinancing', 'insuranceProduct', $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('insuranceinfotitle')"
            :content="retrieveInfoText('insuranceinfotext')"
            data-qa="insurance-info-icon"
          />
        </template>
      </form-radio-button-list>
      <horizontal-line v-if="insuranceSelected" />
      <form-radio-button-list
        v-if="insuranceSelected"
        id="ownRiskLevel"
        data-qa="own-risk-level"
        :title="retrieveModalText('ownRiskLevellabel')"
        :options="productTypes.ownRiskLevel"
        :model-value="financialParams.ownRiskLevel"
        :disabled="disableFields"
        @change="$emit('updateFinancing', 'ownRiskLevel', $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('ownriskinfotitle')"
            :content="retrieveInfoText('ownriskinfotext')"
            data-qa="own-risk-info-icon"
          />
        </template>
      </form-radio-button-list>
      <horizontal-line v-if="isPrivateLease && isPrivateLeasePrePaymentEnabled" />
      <price-input
        v-if="isPrivateLease && isPrivateLeasePrePaymentEnabled"
        id="prePayment-private-lease"
        v-model="financialParams.prePayment"
        name="prePayment"
        :title="retrieveModalText('prePaymentLabel')"
        class="has-info payment-input"
        :tab-on-enter="true"
        tabindex="1"
        :fraction-digits="0"
        :truncate="true"
        :is-read-only="disableFields"
        @change="$emit('updateFinancing', 'prePayment', $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('prepaymenttitle')"
            :content="retrieveInfoText('prepaymenttext')"
            data-qa="lease-pre-payment-info-icon"
          />
        </template>
      </price-input>
      <template v-if="isOperationalLease">
        <horizontal-line />
        <form-radio-button-list
          id="fuel-card"
          data-qa="fuel-card"
          :title="retrieveModalText('fuelcardlabel')"
          :options="productTypes.fuelCardProduct"
          :model-value="financialParams.fuelCardProduct"
          :disabled="disableFields"
          @change="$emit('updateFinancing', 'fuelCardProduct', $event)"
        >
          <template #info>
            <info-modal
              :title="retrieveInfoText('fuelcardinfotitle')"
              :content="retrieveInfoText('fuelcardinfotext')"
              data-qa="fuel-card-info-icon"
            />
          </template>
        </form-radio-button-list>
      </template>
      <horizontal-line />
      <label>{{ retrieveInfoText('includedtitle') }}</label>
      <div class="financing-form__included-info" v-html="includedText"></div>
      <horizontal-line />
      <section-content
        :expandable="true"
        :expand-on-load="!!selectedDiscounts"
        :title="retrieveModalText('correctionslabel')"
        :hide-icon="true"
      >
        <multiple-discounts-component
          :discounts="financialParams.multipleDiscounts || []"
          :disabled="disableFields"
          @discounts-changed="updateMultipleDiscounts"
        />
      </section-content>
      <horizontal-line />
      <section-content
        :expandable="true"
        :expand-on-load="selectedCollectivityDiscount.selected"
        :title="retrieveModalText('otherlabel')"
        :hide-icon="true"
      >
        <correction-items
          :discounts="collectivityDiscount"
          :label-prefix="labelPrefix"
          :disabled="disableFields"
          @toggle-discount="toggleDiscount($event, discountType.code)"
          @update-discount-code="updateDiscountCode"
        ></correction-items>
      </section-content>
      <horizontal-line />
      <section-content
        :expandable="true"
        :expand-on-load="false"
        :title="retrieveModalText('overview/overviewlabel')"
        :hide-icon="true"
      >
        <lease-overview :highlight-price-diff="true" :lease-type="leaseType" />
      </section-content>
      <lease-price-difference-highlight :vwpfs-lease-data="vwpfsLeaseData" />
      <horizontal-line />

      <dl class="total-line" :class="{ 'total-line--group': hasMonthlyPrePayment }">
        <dt>{{ retrieveModalText('totallabel') }}</dt>
        <dl>
          <span class="total-line__value h3" data-qa="lease-modal-price">
            {{ hasMonthlyPrePayment ? formattedRateAmount : formattedLeasePrice }}
          </span>
          &nbsp;{{ retrieveModalText('summarypostfix') }}
        </dl>
      </dl>

      <template v-if="hasMonthlyPrePayment">
        <dl class="total-line total-line--group">
          <dt>{{ retrieveModalText('privatelease/rateprepaymentmonthlylabel') }}</dt>
          <dl>
            <span class="total-line__value h3" data-qa="lease-modal-prepayment-monthly">
              {{ formattedRatePrePaymentMonthly }}
            </span>
            &nbsp;{{ retrieveModalText('summarypostfix') }}
          </dl>
        </dl>
        <dl class="total-line total-line--highlight">
          <dt>{{ retrieveModalText('privatelease/rateamountafterprepaymentlabel') }}</dt>
          <dl>
            <span class="total-line__value h3" data-qa="lease-modal-price-after-prepayment">
              {{ formattedLeasePrice }}
            </span>
            &nbsp;{{ retrieveModalText('summarypostfix') }}
          </dl>
        </dl>
      </template>
      <div class="disclaimer">
        <img
          v-if="!isOperationalLease"
          :src="`/wwwroot/images/privatelease/private-lease-keurmerk.png`"
          data-qa="private-lease-image"
          alt="Keurmerk Private Lease"
        />
        <div data-qa="lease-modal-disclaimer" v-html="retrieveInfoText('disclaimertext')"></div>
      </div>
      <div v-if="quotationId" class="calculated-id-container">
        <p>
          <span>{{ retrieveModalText('calculationNumber') }}</span>
          {{ quotationId }}
        </p>
      </div>
      <div class="button-container">
        <app-button
          type="submit"
          form="financing-form"
          data-qa="lease-modal-submit"
          @click="$emit('submit')"
        >
          {{ retrieveModalText('confirmbutton') }}
        </app-button>
        <app-button
          v-if="showApplyForLease"
          :disabled="hasNoLeasePrice"
          type="button"
          data-qa="lease-request"
          @click="applyForLease"
        >
          {{ retrieveModalText('redirectTo') }}
        </app-button>
      </div>
      <div v-if="isLoading" class="overlay">
        <loader />
      </div>
    </form>
  </div>
</template>

<script>
import FormSelect from '@/components/generic/presentational/atoms/formSelect/FormSelect.vue'
import FormRadioToggle from '@/components/generic/presentational/atoms/formRadioToggle/FormRadioToggle.vue'
import FormRadioButtonList from '@/components/generic/presentational/atoms/formRadioButtonList/FormRadioButtonList.vue'
import InfoModal from '@/components/generic/presentational/atoms/infoModal/InfoModal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import FinancialServiceForm from '@/mixins/financialServices/financialServiceForm'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import CurrencyHelper from '@/helpers/currencyHelper'
import SectionContent from '@/components/generic/presentational/atoms/sectionContent/SectionContent.vue'
import LeaseOverview from '@/components/generic/container/FinancialServices/Lease/LeaseOverview/LeaseOverview.vue'
import TyresAccessories from '@/components/generic/container/TyresAccessories.vue'
import CorrectionItems from '@/components/generic/presentational/molecules/addCorrection/CorrectionItems.vue'
import MultipleDiscountsComponent from '@/components/generic/presentational/molecules/addCorrection/MultipleDiscountsComponent.vue'
import discountType from '@/helpers/enums/discountType'
import PriceInput from '@/components/generic/presentational/atoms/priceInput/PriceInput.vue'
import { ref } from 'vue'
import constants from '@/utils/constants'
import LeasePriceDifferenceHighlight from '@/components/generic/container/FinancialServices/Lease/LeaseOverview/LeasePriceDifferenceHighlight.vue'
import { mapState } from 'vuex'

export default {
  components: {
    LeasePriceDifferenceHighlight,
    FormRadioToggle,
    FormRadioButtonList,
    FormSelect,
    AppButton,
    Loader,
    HorizontalLine,
    InfoModal,
    SectionContent,
    CorrectionItems,
    MultipleDiscountsComponent,
    LeaseOverview,
    TyresAccessories,
    PriceInput,
  },
  mixins: [FinancialServiceForm],
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    productTypes: {
      type: Object,
      required: true,
    },
    dictionaryFolder: {
      type: String,
      required: true,
    },
    quotationId: {
      type: Number,
      required: false,
      default: null,
    },
    showApplyForLease: {
      type: Boolean,
      required: true,
    },
    leaseType: {
      type: String,
      required: true,
    },
    vwpfsLeaseData: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'submit',
    'updateFinancing',
    'applyForLease',
    'updateDiscount',
    'updateMultipleDiscounts',
  ],
  setup() {
    const tyresAccessories = ref(null)
    return {
      tyresAccessories,
    }
  },
  data() {
    return {
      discountType: discountType,
      infoText: {
        tireProduct: {
          body: '',
          show: false,
        },
        reliefVehicleProduct: {
          body: '',
          show: false,
        },
        ownRiskLevel: {
          body: '',
          show: false,
        },
      },
      errors: null,
      discounts: [],
      collectivityDiscount: [
        {
          id: 'collectivityDiscountLabel',
          type: discountType.code,
          selected: !!this.financialParams.collectivityCode,
          code: this.financialParams.collectivityCode,
        },
      ],
      multipleDiscounts: [],
      leaseTypes: constants.leaseTypes,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    isOperationalLease() {
      return this.leaseType === this.leaseTypes.OPERATIONAL_LEASE
    },
    isPrivateLeasePrePaymentEnabled() {
      return this.dealerSettings.privateLeasePrePaymentEnabled
    },
    isPrivateLease() {
      return this.leaseType === this.leaseTypes.PRIVATE_LEASE
    },
    insuranceSelected() {
      return !this.isOperationalLease || this.financialParams.insuranceProduct == 'WCS'
    },
    labelPrefix() {
      return this.isOperationalLease
        ? '/financialservices/operationallease/modal'
        : '/financialservices/lease/modal'
    },
    modalLabels() {
      return {
        tire: {
          body: this.retrieveInfoText('tireinfotext'),
          label: this.retrieveInfoText('tirepricelabel'),
        },
        reliefVehicle: {
          body: this.retrieveInfoText('reliefVehicleinfotext'),
          label: this.retrieveInfoText('reliefVehiclelabel'),
        },
        ownRiskLevel: {
          body: this.retrieveInfoText('ownRiskLevelinfotext'),
          label: this.retrieveInfoText('ownRiskLevellabel'),
        },
      }
    },
    yearlyKmInfoText() {
      return this.retrieveModalText('yearlykminfotext', {
        yearlyKm: this.i18n.n(this.financialParams.yearlyKm),
        excessKmPriceCt: this.i18n.n(this.financialParams.excessKmPriceCt, 'number'),
        lessKmPriceCt: this.i18n.n(this.financialParams.lessKmPriceCt, 'number'),
      })
    },
    lessYearlyKmInfoText() {
      if (!this.isOperationalLease) return null

      return this.retrieveModalText('lessyearlykminfotext', {
        yearlyKm: this.i18n.n(this.financialParams.yearlyKm),
        excessKmPriceCt: this.i18n.n(this.financialParams.excessKmPriceCt, 'number'),
        lessKmPriceCt: this.i18n.n(this.financialParams.lessKmPriceCt, 'number'),
      })
    },
    includedText() {
      let labelKey = 'includedtext'
      if (this.isOperationalLease && this.insuranceSelected) {
        labelKey = 'includedtextwithinsurance'
      }
      return this.retrieveInfoText(labelKey)
    },
    yearlyKmDisabledOptions() {
      let duration = this.financialParams.duration
      if (duration == 60) return this.productTypes.yearlyKm.filter((opt) => opt.value >= 40000)
      if (duration == 54) return this.productTypes.yearlyKm.filter((opt) => opt.value >= 45000)
      if (duration == 48) return this.productTypes.yearlyKm.filter((opt) => opt.value >= 50000)
      return []
    },
    durationDisabledOptions() {
      let distance = this.financialParams.yearlyKm
      if (distance == 40000) return this.productTypes.duration.filter((opt) => opt.value >= 60)
      if (distance == 45000) return this.productTypes.duration.filter((opt) => opt.value >= 54)
      if (distance == 50000) return this.productTypes.duration.filter((opt) => opt.value >= 48)
      return []
    },
    hasNoLeasePrice() {
      return this.financialParams.noPrivateLeaseAvailable || !this.financialParams.leasePrice
    },
    formattedLeasePrice() {
      return this.hasNoLeasePrice ? '-' : CurrencyHelper.currency(this.financialParams.leasePrice)
    },

    hasMonthlyPrePayment() {
      return this.isPrivateLease && parseFloat(this.financialParams.ratePrePaymentMonthly) > 0
    },
    formattedRateAmount() {
      if (this.hasNoLeasePrice) return '-'
      return CurrencyHelper.currency(
        this.financialParams.rateAmount ?? this.financialParams.leasePrice
      )
    },
    formattedRatePrePaymentMonthly() {
      return CurrencyHelper.currency(this.financialParams.ratePrePaymentMonthly)
    },

    disableFields() {
      return (
        this.isReadOnly ||
        this.financialParams.configurationInvalid ||
        this.financialParams.noPrivateLeaseAvailable
      )
    },
    noPrivateLeaseErrorMessage() {
      return this.financialParams.noPrivateLeaseAvailable
        ? this.i18n.t('/financialservices/lease/modal/privatelease/noprivateleasevalueerror')
        : null
    },
    errorMessages() {
      if (this.financialParams.errors?.length) {
        return this.financialParams.errors.filter(
          (error) => !!error.message && error.message != 'null'
        )
      }

      return null
    },
    selectedDiscounts() {
      // Return truthy value if there are multiple discounts to expand the section
      return (
        this.financialParams.multipleDiscounts && this.financialParams.multipleDiscounts.length > 0
      )
    },
    selectedCollectivityDiscount() {
      return this.collectivityDiscount[0]
    },
    allTyresOptionSelected() {
      return this.financialParams.tireProduct === 'A'
    },
  },
  methods: {
    applyForLease() {
      this.$emit('applyForLease')
    },
    toggleDiscount(index, type = null) {
      if (type === discountType.code) {
        const toggledDiscount = this.collectivityDiscount[index]
        toggledDiscount.selected = !toggledDiscount.selected
        this.$emit('updateDiscount', toggledDiscount)
      }
    },
    updateDiscountCode(code) {
      this.selectedCollectivityDiscount.code = code
      this.$emit('updateDiscount', this.selectedCollectivityDiscount)
    },
    updateMultipleDiscounts(discounts) {
      this.multipleDiscounts = discounts
      this.$emit('updateMultipleDiscounts', discounts)
    },
    changeTyresSelection(value) {
      let oldValue = this.financialParams.tireProduct
      this.$emit('updateFinancing', 'tireProduct', value)
      if (value === 'A') {
        this.tyresAccessories.checkOpenEditModal()
      } else if (oldValue === 'A') {
        this.tyresAccessories.resetAllWeatherTyresPL(true)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.financing-form {
  margin-bottom: 1rem;
  &__info-text {
    font-style: italic;
    display: block;
  }

  .calculated-id-container {
    span {
      font-weight: $font-weight-bold;
      font-size: $font-size-base * 1.25;
    }
  }

  .button-container {
    display: flex;
    justify-content: space-between;
  }

  label {
    display: block;
    font-weight: $font-weight-bold;
    margin-bottom: 0.5rem;
  }
  &__included-info:deep() {
    ul {
      padding-left: 2rem;
    }
  }

  .disclaimer {
    img {
      float: right;
    }
  }

  :deep() {
    .product-type {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin: 0.5em 0;

      .label {
        flex-basis: 12rem;
        font-size: $font-size-base * 1.25;
        color: $black;
      }

      .input {
        flex-basis: 22rem;
        margin-right: 0;
      }
    }

    .section--expandable {
      h4 {
        font-size: $font-size-base * 1.25;
        color: $black;
      }
      .title {
        border-bottom: 0;
      }
    }

    .has-info {
      & label {
        width: 10rem;
      }

      & .info {
        flex-basis: 2rem;
      }

      & .input {
        margin-left: 0;
      }
    }
  }

  .payment-input {
    flex-basis: 100%;
    :deep(input) {
      max-width: 200px;
    }
  }
}

.form-item {
  & + & {
    margin-top: 1rem;
  }
}

.total-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  &__value {
    color: $color-primary;
    font-weight: $font-weight-bold;
  }
  &--group {
    margin-bottom: 0.5rem;
  }
  &--highlight {
    dt {
      font-weight: $font-weight-bold;
    }
  }
}

.error-message {
  color: $color-danger;
  font-size: 0.8em;
}
</style>
